import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Spinner, Alert, Nav, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { addEnquiryApi, getEnquiryAnswersApi, markEnquiryReadApi } from '../../api/enquiry';
import { getCreatedClasses } from '../../api/classes';

export const EnquiryPage = () => {
  // State for enquiries list
  const [enquiries, setEnquiries] = useState([]);
  const [loadingEnquiries, setLoadingEnquiries] = useState(true);
  const [enquiryError, setEnquiryError] = useState(null);

  // State for classes created by me
  const [myCreatedClasses, setMyCreatedClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  // Form states for new enquiry
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Filtering & search states
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'unread', 'answered', 'pending'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClassFilter, setSelectedClassFilter] = useState('all');

  // Action state for marking as read
  const [markingReadId, setMarkingReadId] = useState(null);

  // Fetch enquiries and created classes on mount
  useEffect(() => {
    fetchEnquiries();
    fetchCreatedClasses();
  }, []);

  // Fetch enquiries list (GET /enquiry)
  const fetchEnquiries = () => {
    setLoadingEnquiries(true);
    setEnquiryError(null);
    getEnquiryAnswersApi()
      .then((res) => {
        let list = [];
        if (Array.isArray(res)) {
          list = res;
        } else if (res && Array.isArray(res.data)) {
          list = res.data;
        } else if (res && Array.isArray(res.enquiries)) {
          list = res.enquiries;
        } else if (res && Array.isArray(res.result)) {
          list = res.result;
        }
        setEnquiries(list);
      })
      .catch((err) => {
        console.error('Error fetching enquiries:', err);
        setEnquiryError('Failed to load enquiries. Please check your network or login session.');
      })
      .finally(() => {
        setLoadingEnquiries(false);
      });
  };

  // Fetch classes created by me (GET /classes/created-by-me)
  const fetchCreatedClasses = () => {
    setLoadingClasses(true);
    getCreatedClasses()
      .then((res) => {
        let classList = [];
        if (Array.isArray(res)) {
          classList = res;
        } else if (res && Array.isArray(res.data)) {
          classList = res.data;
        } else if (res && Array.isArray(res.classes)) {
          classList = res.classes;
        } else if (res && res.result && Array.isArray(res.result)) {
          classList = res.result;
        }
        setMyCreatedClasses(classList);
      })
      .catch((err) => {
        console.warn('Could not fetch created classes:', err);
      })
      .finally(() => {
        setLoadingClasses(false);
      });
  };

  // Handle new enquiry submission (POST /enquiry)
  const handleSubmitEnquiry = (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setSubmitError('Please enter your enquiry question or message.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    const questionContent = subject.trim()
      ? `[${subject.trim()}] ${message.trim()}`
      : message.trim();

    const payload = {
      question: questionContent,
      subject: subject.trim(),
      message: message.trim(),
      class_id: selectedClassId || null,
    };

    addEnquiryApi(payload)
      .then(() => {
        setSubmitSuccess(true);
        setSubject('');
        setMessage('');
        setSelectedClassId('');
        fetchEnquiries();
        setTimeout(() => setSubmitSuccess(false), 4000);
      })
      .catch((err) => {
        console.error('Failed to submit enquiry:', err);
        setSubmitError(err?.response?.data?.message || 'Failed to submit enquiry. Please try again.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  // Handle marking an enquiry as read (PATCH/POST /enquiry/read)
  const handleMarkAsRead = (enquiryId) => {
    if (!enquiryId) return;
    setMarkingReadId(enquiryId);
    markEnquiryReadApi(enquiryId)
      .then(() => {
        setEnquiries((prev) =>
          prev.map((item) => {
            const id = item.id || item._id || item.enquiry_id;
            if (String(id) === String(enquiryId)) {
              return { ...item, is_read: true, read: true, isRead: true, status: item.status || 'read' };
            }
            return item;
          })
        );
      })
      .catch((err) => {
        console.error('Failed to mark enquiry as read:', err);
      })
      .finally(() => {
        setMarkingReadId(null);
      });
  };

  // Filter logic
  const filteredEnquiries = enquiries.filter((item) => {
    const qText = (item.question || item.message || item.subject || '').toLowerCase();
    const aText = (item.answer || item.reply || item.response || '').toLowerCase();
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || qText.includes(searchLower) || aText.includes(searchLower);

    // Class filter
    const itemClassId = item.class_id || item.classId || (item.class && (item.class.id || item.class._id));
    const matchesClass = selectedClassFilter === 'all' || String(itemClassId) === String(selectedClassFilter);

    // Tab filter
    const hasAnswer = Boolean(item.answer || item.reply || item.response || item.answer_text);
    const isRead = Boolean(item.is_read || item.read || item.isRead);

    if (!matchesSearch || !matchesClass) return false;

    if (activeTab === 'unread') return hasAnswer && !isRead;
    if (activeTab === 'answered') return hasAnswer;
    if (activeTab === 'pending') return !hasAnswer;

    return true;
  });

  // Calculate statistics
  const totalCount = enquiries.length;
  const answeredCount = enquiries.filter((item) => Boolean(item.answer || item.reply || item.response || item.answer_text)).length;
  const unreadCount = enquiries.filter((item) => {
    const hasAns = Boolean(item.answer || item.reply || item.response || item.answer_text);
    const isRead = Boolean(item.is_read || item.read || item.isRead);
    return hasAns && !isRead;
  }).length;
  const pendingCount = totalCount - answeredCount;

  return (
    <div className="enquiry-page bg-light min-vh-100 pb-5">
      {/* Banner */}
      <section className="bg-primary text-white py-5 mb-4 shadow-sm position-relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #4A00E0 0%, #8E2DE2 100%)' }}>
        <Container fluid="lg">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-2 text-white-50 text-uppercase fw-semibold text-xs mb-2">
                <Link to="/" className="text-white-50 text-decoration-none hover-white">Home</Link>
                <span>/</span>
                <span className="text-white">Enquiry & Support</span>
              </div>
              <h1 className="fw-bold display-6 mb-2">Help & Enquiry Center</h1>
              <p className="lead text-white-50 mb-0">
                Submit questions, enquire about your classes, track admin responses, and get answers fast.
              </p>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button
                variant="light"
                className="fw-bold rounded-pill px-4 shadow-sm text-primary"
                onClick={fetchEnquiries}
                disabled={loadingEnquiries}
              >
                {loadingEnquiries ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh Enquiries
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container fluid="lg">
        {/* Quick Stats Cards */}
        <Row className="g-3 mb-4">
          <Col xs={6} md={3}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="d-flex align-items-center p-3">
                <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3 text-primary d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-question-circle fs-4"></i>
                </div>
                <div>
                  <h3 className="fw-bold mb-0 text-dark">{totalCount}</h3>
                  <small className="text-muted fw-semibold">Total Enquiries</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="d-flex align-items-center p-3">
                <div className="rounded-circle bg-warning bg-opacity-10 p-3 me-3 text-warning d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-envelope-badge fs-4"></i>
                </div>
                <div>
                  <h3 className="fw-bold mb-0 text-dark">{unreadCount}</h3>
                  <small className="text-muted fw-semibold">Unread Replies</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="d-flex align-items-center p-3">
                <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3 text-success d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-check-circle fs-4"></i>
                </div>
                <div>
                  <h3 className="fw-bold mb-0 text-dark">{answeredCount}</h3>
                  <small className="text-muted fw-semibold">Answered</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="d-flex align-items-center p-3">
                <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3 text-info d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                  <i className="bi bi-hourglass-split fs-4"></i>
                </div>
                <div>
                  <h3 className="fw-bold mb-0 text-dark">{pendingCount}</h3>
                  <small className="text-muted fw-semibold">Pending Review</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4">
          {/* Left Column: Create New Enquiry Form */}
          <Col lg={5}>
            <Card className="border-0 shadow-sm rounded-4 sticky-lg-top" style={{ top: '90px' }}>
              <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
                <div className="d-flex align-items-center gap-2">
                  <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                    <i className="bi bi-pencil-fill"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold mb-0 text-dark">Submit New Enquiry</h5>
                    <small className="text-muted">Fill in your question or support details</small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {submitSuccess && (
                  <Alert variant="success" className="rounded-3 d-flex align-items-center gap-2" onClose={() => setSubmitSuccess(false)} dismissible>
                    <i className="bi bi-check-circle-fill fs-5"></i>
                    <div>
                      <strong>Enquiry Submitted!</strong>
                      <div className="text-xs">Your question has been logged successfully. We will get back to you soon.</div>
                    </div>
                  </Alert>
                )}

                {submitError && (
                  <Alert variant="danger" className="rounded-3" onClose={() => setSubmitError('')} dismissible>
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {submitError}
                  </Alert>
                )}

                <Form onSubmit={handleSubmitEnquiry}>
                  {/* Subject Title */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-dark text-sm">Subject / Title</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Schedule inquiry, Payment issue, Class content"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="rounded-3 shadow-none"
                    />
                  </Form.Group>

                  {/* Question / Message */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark text-sm">
                      Question / Message Detail <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Type your enquiry details here..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="rounded-3 shadow-none"
                      required
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 py-2.5 rounded-pill fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Submitting Enquiry...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send-fill"></i>
                        Submit Enquiry
                      </>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column: Enquiries List & Answers */}
          <Col lg={7}>
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-3">
                  <div>
                    <h5 className="fw-bold mb-0 text-dark">My Enquiries & Answers</h5>
                    <small className="text-muted">Track status, read responses, and manage your tickets</small>
                  </div>
                  {/* Search Input */}
                  <div style={{ maxWidth: '240px' }}>
                    <InputGroup size="sm">
                      <InputGroup.Text className="bg-light border-end-0">
                        <i className="bi bi-search text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search enquiries..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-light border-start-0 shadow-none"
                      />
                    </InputGroup>
                  </div>
                </div>

                {/* Filter Tabs */}
                <Nav variant="tabs" className="border-bottom-0">
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === 'all'}
                      onClick={() => setActiveTab('all')}
                      className="fw-semibold px-3 text-sm cursor-pointer"
                    >
                      All ({totalCount})
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === 'unread'}
                      onClick={() => setActiveTab('unread')}
                      className="fw-semibold px-3 text-sm cursor-pointer"
                    >
                      Unread Answers
                      {unreadCount > 0 && <Badge bg="danger" pill className="ms-1.5">{unreadCount}</Badge>}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === 'answered'}
                      onClick={() => setActiveTab('answered')}
                      className="fw-semibold px-3 text-sm cursor-pointer"
                    >
                      Answered ({answeredCount})
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      active={activeTab === 'pending'}
                      onClick={() => setActiveTab('pending')}
                      className="fw-semibold px-3 text-sm cursor-pointer"
                    >
                      Pending ({pendingCount})
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Header>

              <Card.Body className="p-4">
                {/* Filter by Created Class option if user has created classes */}
                {myCreatedClasses.length > 0 && (
                  <div className="d-flex align-items-center gap-2 mb-3 p-2.5 bg-light rounded-3">
                    <i className="bi bi-funnel-fill text-primary"></i>
                    <span className="text-xs fw-bold text-dark text-nowrap">Filter by Created Class:</span>
                    <Form.Select
                      size="sm"
                      value={selectedClassFilter}
                      onChange={(e) => setSelectedClassFilter(e.target.value)}
                      className="border-0 bg-white shadow-none rounded-2"
                      style={{ maxWidth: '260px' }}
                    >
                      <option value="all">All Classes & General</option>
                      {myCreatedClasses.map((cls) => {
                        const cId = cls.id || cls._id || cls.class_id;
                        return (
                          <option key={cId} value={cId}>
                            {cls.title || cls.name || cls.class_name || `Class #${cId}`}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </div>
                )}

                {loadingEnquiries ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" className="mb-2" />
                    <p className="text-muted text-sm mb-0">Loading your enquiries...</p>
                  </div>
                ) : enquiryError ? (
                  <Alert variant="danger" className="rounded-3 my-3">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {enquiryError}
                    <div className="mt-2">
                      <Button variant="outline-danger" size="sm" onClick={fetchEnquiries}>
                        Try Again
                      </Button>
                    </div>
                  </Alert>
                ) : filteredEnquiries.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="rounded-circle bg-light d-inline-flex p-4 mb-3 text-muted">
                      <i className="bi bi-inbox fs-1"></i>
                    </div>
                    <h6 className="fw-bold text-dark mb-1">No Enquiries Found</h6>
                    <p className="text-muted text-sm mb-0" style={{ maxWidth: '360px', margin: '0 auto' }}>
                      {searchQuery
                        ? `No enquiry matches "${searchQuery}". Try a different search term.`
                        : activeTab !== 'all'
                          ? `No enquiries under "${activeTab}" category.`
                          : 'You haven\'t submitted any support enquiries yet. Use the form on the left to ask a question!'}
                    </p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {filteredEnquiries.map((enq, index) => {
                      const id = enq.id || enq._id || enq.enquiry_id || index;
                      const questionText = enq.question || enq.subject || enq.message || 'Enquiry question';
                      const answerText = enq.answer || enq.reply || enq.response || enq.answer_text;
                      const createdAt = enq.created_at || enq.createdAt || enq.date || 'Recent';
                      const isRead = Boolean(enq.is_read || enq.read || enq.isRead);
                      const relatedClassId = enq.class_id || enq.classId;
                      const matchedClass = myCreatedClasses.find(
                        (c) => String(c.id || c._id || c.class_id) === String(relatedClassId)
                      );

                      return (
                        <Card
                          key={id}
                          className={`border rounded-3 transition-all ${answerText && !isRead ? 'border-warning shadow-sm bg-warning bg-opacity-10' : 'border-light-subtle'
                            }`}
                        >
                          <Card.Body className="p-3.5">
                            {/* Card Top: Badges and Timestamps */}
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                              <div className="d-flex align-items-center gap-2">
                                {answerText ? (
                                  <Badge bg="success" className="px-2.5 py-1 rounded-pill fw-semibold text-xs">
                                    <i className="bi bi-check-circle-fill me-1"></i> Answered
                                  </Badge>
                                ) : (
                                  <Badge bg="secondary" className="px-2.5 py-1 rounded-pill fw-semibold text-xs">
                                    <i className="bi bi-clock-history me-1"></i> Pending
                                  </Badge>
                                )}

                                {answerText && (
                                  isRead ? (
                                    <Badge bg="light" text="dark" className="border px-2 py-1 rounded-pill text-xs">
                                      <i className="bi bi-check2-all text-primary me-1"></i> Read
                                    </Badge>
                                  ) : (
                                    <Badge bg="danger" className="px-2 py-1 rounded-pill text-xs">
                                      Unread Reply
                                    </Badge>
                                  )
                                )}

                                {(matchedClass || relatedClassId) && (
                                  <Badge bg="info" text="dark" className="px-2.5 py-1 rounded-pill text-xs bg-opacity-25">
                                    <i className="bi bi-journal-bookmark me-1"></i>
                                    {matchedClass?.title || matchedClass?.name || `Class #${relatedClassId}`}
                                  </Badge>
                                )}
                              </div>

                              <small className="text-muted text-xs">
                                <i className="bi bi-calendar3 me-1"></i>
                                {typeof createdAt === 'string' && createdAt.includes('T')
                                  ? new Date(createdAt).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })
                                  : createdAt}
                              </small>
                            </div>

                            {/* Question Section */}
                            <div className="mb-2">
                              <h6 className="fw-bold text-dark mb-1 text-sm">
                                <i className="bi bi-patch-question text-primary me-2"></i>
                                Question:
                              </h6>
                              <p className="text-dark text-sm mb-0 ms-4 bg-light p-2.5 rounded-3 border-start border-3 border-primary" style={{ whiteSpace: 'pre-wrap' }}>
                                {questionText}
                              </p>
                            </div>

                            {/* Answer Section */}
                            {answerText ? (
                              <div className="mt-3 pt-2 border-top">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                  <h6 className="fw-bold text-success mb-0 text-sm">
                                    <i className="bi bi-chat-left-dots-fill me-2"></i>
                                    Response / Answer:
                                  </h6>
                                  {!isRead && (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="py-0 px-2.5 text-xs rounded-pill shadow-sm fw-semibold"
                                      disabled={markingReadId === id}
                                      onClick={() => handleMarkAsRead(id)}
                                    >
                                      {markingReadId === id ? (
                                        <>
                                          <Spinner animation="border" size="sm" className="me-1" />
                                          Marking...
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-check2 me-1"></i>
                                          Mark as Read
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </div>
                                <div className="p-3 bg-success bg-opacity-10 rounded-3 text-dark text-sm border-start border-3 border-success ms-4" style={{ whiteSpace: 'pre-wrap' }}>
                                  {answerText}
                                </div>
                              </div>
                            ) : (
                              <div className="mt-2 text-muted text-xs ms-4">
                                <i className="bi bi-info-circle me-1"></i>
                                Our support team is reviewing your question. Answers will appear here.
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EnquiryPage;
